/**
 * OverridesTab 客戶特殊價 (S9.1) — customer × product special prices.
 *
 * - BOTH variants (recheck-verified refinement): fixed-price AND override-%
 *   (the legacy 175% case is a markup-% override of the Pricing Base).
 * - Validity windows (validFrom/validTo, open-ended allowed) — this IS the
 *   effective-dating story for the demo; versioned price lists are DESCOPED
 *   (plan §S9.1, cut by default).
 * - EVERY save goes through ReasonRequiredModal → a PRICE_OVERRIDE audit row
 *   (the「報表送出給高權限看過」answer, rendered below as the review lens;
 *   S10.1 lifts it into the governance audit page). A blank reason is blocked
 *   by the store guard, never defaulted.
 *
 * S3.1 batch mode (DA-2 explicit-selection MVP) alongside the single-pair
 * flow — visibility-gated on `canEditCostBasis()` (DA-7, hide-don't-disable;
 * the single-pair flow keeps its open gating):
 * - EXPLICIT multi-select of products AND customers — never a range selector
 *   (R-1: nothing qty-conditioned anywhere on this form); hard cap 50 combos
 *   with a NAMED cap-exceeded state (over the cap nothing is resolved).
 * - ONE adjustment for all combos: markup_pct, or fixed with the FREEZE
 *   vocabulary (凍結價格 — a frozen price detaches from cost/ladder changes).
 * - PREVIEW = exactly the rows to be written, each showing the CURRENT
 *   resolved price next to the engine-resolved proposed value.
 * - OVERLAP REFUSAL (RULING-5, 2026-08-29): a combo whose pair carries ANY
 *   active special — window ignored — is flagged and blocks Apply until
 *   deselected or the standing row is deactivated (store guard BATCH_OVERLAP
 *   is the hard backstop — whole-batch refusal, never a partial write).
 * - UNRESOLVABLE REFUSAL (RULING-6, 2026-08-29): a markup-% draft naming a
 *   product with no resolvable cost record is flagged and blocks Apply — the
 *   row could never resolve (and would activate unreviewed when a cost
 *   record later appears). Freeze rows need no base and stay legal (store
 *   guard BATCH_UNRESOLVABLE is the hard backstop).
 * - ONE reason for the batch → ONE 'special_price_batch' audit header +
 *   per-row entries, written atomically.
 *
 * S3.2: reads the `?product=` query param (the product-detail "Manage
 * special prices" link) and prefilters the list to that product.
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Col,
  DatePicker,
  Dropdown,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import type { TableProps } from 'antd';
import { AppstoreAddOutlined, MoreOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import { HighlightText } from '@/components/common/HighlightText';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import { DEFAULT_PRICING_ANCHOR, effectiveAnchorOf } from '@/lib/pricing';
import { useCurrencies } from '@/hooks/useCurrencies';
import { useCustomerOptions } from '@/hooks/useCustomers';
import { useProductOptions } from '@/hooks/useProducts';
import {
  buildSpecialPriceBatchPreview,
  canEditCostBasis,
  usePriceOverrideAudit,
  usePricingDataset,
  usePricingMutations,
} from '@/hooks/usePricing';
import type { SpecialPriceBatchPreviewRow } from '@/hooks/usePricing';
import { PricingGuardError, SPECIAL_PRICE_BATCH_CAP } from '@/stores/pricingStore';
import type { PriceOverrideAuditRow, SpecialPriceBatchInput } from '@/stores/pricingStore';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import type { PriceResolution, SpecialItemPrice } from '@/types/api/pricing';
import { formatMoney } from '../salesOrders/components/soFormat';

const { Text, Title } = Typography;

// ---------------------------------------------------------------------------
// Form contract — discriminated by kind, refined cross-field (zodResolver only)
// ---------------------------------------------------------------------------

const overrideFormSchema = z
  .object({
    customerId: z.number().min(1, 'pricing.overrides.modal.validation.customerRequired'),
    productId: z.string().min(1, 'pricing.overrides.modal.validation.productRequired'),
    kind: z.enum(['fixed', 'markup_pct']),
    fixedPrice: z.number().nullable(),
    currency: z.string().nullable(),
    markupPct: z.number().nullable(),
    validFrom: z.string().min(1, 'pricing.overrides.modal.validation.validFromRequired'),
    validTo: z.string().nullable(),
  })
  .superRefine((values, ctx) => {
    if (values.kind === 'fixed') {
      if (values.fixedPrice === null || values.fixedPrice <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fixedPrice'],
          message: 'pricing.overrides.modal.validation.fixedPriceRequired',
        });
      }
      if (!values.currency) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['currency'],
          message: 'pricing.overrides.modal.validation.currencyRequired',
        });
      }
    } else if (values.markupPct === null || values.markupPct <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['markupPct'],
        message: 'pricing.overrides.modal.validation.markupPctRequired',
      });
    }
    if (values.validTo && values.validFrom && values.validTo < values.validFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['validTo'],
        message: 'pricing.overrides.modal.validation.validToAfterFrom',
      });
    }
  });

type OverrideFormData = z.infer<typeof overrideFormSchema>;

const defaultFormValues: OverrideFormData = {
  customerId: 0,
  productId: '',
  kind: 'fixed',
  fixedPrice: null,
  currency: null,
  markupPct: null,
  validFrom: dayjs().format('YYYY-MM-DD'),
  validTo: null,
};

// ---------------------------------------------------------------------------
// S3.1 batch form contract — explicit selections only (R-1: no ranges, no
// quantity conditions), ONE adjustment, one validity window
// ---------------------------------------------------------------------------

const batchFormSchema = z
  .object({
    productIds: z
      .array(z.string())
      .min(1, 'pricing.overrides.batch.validation.productsRequired'),
    customerIds: z
      .array(z.number())
      .min(1, 'pricing.overrides.batch.validation.customersRequired'),
    kind: z.enum(['fixed', 'markup_pct']),
    fixedPrice: z.number().nullable(),
    currency: z.string().nullable(),
    markupPct: z.number().nullable(),
    validFrom: z.string().min(1, 'pricing.overrides.modal.validation.validFromRequired'),
    validTo: z.string().nullable(),
  })
  .superRefine((values, ctx) => {
    if (values.kind === 'fixed') {
      if (values.fixedPrice === null || values.fixedPrice <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fixedPrice'],
          message: 'pricing.overrides.batch.validation.frozenPriceRequired',
        });
      }
      if (!values.currency) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['currency'],
          message: 'pricing.overrides.modal.validation.currencyRequired',
        });
      }
    } else if (values.markupPct === null || values.markupPct <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['markupPct'],
        message: 'pricing.overrides.modal.validation.markupPctRequired',
      });
    }
    if (values.validTo && values.validFrom && values.validTo < values.validFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['validTo'],
        message: 'pricing.overrides.modal.validation.validToAfterFrom',
      });
    }
  });

type BatchFormData = z.infer<typeof batchFormSchema>;

const defaultBatchValues: BatchFormData = {
  productIds: [],
  customerIds: [],
  kind: 'markup_pct',
  fixedPrice: null,
  currency: null,
  markupPct: null,
  validFrom: dayjs().format('YYYY-MM-DD'),
  validTo: null,
};

/** Pending action carried into the ReasonRequiredModal step. */
type PendingAction =
  | { type: 'save'; row: SpecialItemPrice }
  | { type: 'toggle'; id: string; isActive: boolean }
  | { type: 'batch'; input: SpecialPriceBatchInput };

const OverridesTab = () => {
  const { t } = useTranslation(['pricing', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  const { dataset, isLoading, error, refresh } = usePricingDataset();
  const { saveSpecialPrice, setSpecialPriceActive, saveSpecialPriceBatch } = usePricingMutations();
  const auditRows = usePriceOverrideAudit();
  const { customers } = useCustomerOptions();
  const { products } = useProductOptions();
  const { currencyOptions } = useCurrencies();
  // DA-7 — the ONE visibility gate for the batch seat (single-pair flow keeps
  // its open gating; hide-don't-disable).
  const batchSeat = canEditCostBasis();

  // S3.2 — the product-detail "Manage special prices" link lands here with
  // `?product=`; the list prefilters to that product until cleared.
  const [searchParams, setSearchParams] = useSearchParams();
  const productParam = searchParams.get('product');
  const clearProductFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('product');
    setSearchParams(next, { replace: true });
  };

  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<SpecialItemPrice | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  // Platform currency list + every currency already present in the pricing
  // dataset (bands/cost records) — keeps the fixed-price currency Select
  // usable when the /api/currencies mock endpoint is absent (worktree gap).
  const currencySelectOptions = useMemo(() => {
    const known = new Set(currencyOptions.map((option) => option.value as string));
    const datasetCurrencies = new Set<string>([
      ...(dataset?.priceGroupBands ?? []).map((band) => band.currency),
      ...(dataset?.costRecords ?? []).map((record) => record.currency),
    ]);
    const extras = Array.from(datasetCurrencies)
      .filter((code) => !known.has(code))
      .sort()
      .map((code) => ({ label: code, value: code }));
    return [...currencyOptions, ...extras];
  }, [currencyOptions, dataset]);

  const customerName = useMemo(() => {
    const byId = new Map(customers.map((c) => [c.customerId, c.customerName]));
    return (id: number) => byId.get(id) ?? `#${id}`;
  }, [customers]);

  const productName = useMemo(() => {
    const byId = new Map(products.map((p) => [p.id, p.displayName]));
    return (id: string) => byId.get(id) ?? id;
  }, [products]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = dataset?.specialItemPrices ?? [];
    // S3.2 product prefilter first (query-param scoped), then the text search.
    const scoped = productParam ? all.filter((row) => row.productId === productParam) : all;
    if (!term) return scoped;
    return scoped.filter((row) =>
      [customerName(row.customerId), productName(row.productId), row.reason]
        .join(' ')
        .toLowerCase()
        .includes(term)
    );
  }, [dataset, search, customerName, productName, productParam]);

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = useForm<OverrideFormData>({
    resolver: zodResolver(overrideFormSchema),
    defaultValues: defaultFormValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const watchedKind = useWatch({ control, name: 'kind' });

  // -------------------------------------------------------------------------
  // S3.1 batch form + live preview
  // -------------------------------------------------------------------------

  const {
    control: batchControl,
    handleSubmit: handleBatchSubmit,
    reset: resetBatch,
    trigger: triggerBatch,
    formState: { errors: batchErrors },
  } = useForm<BatchFormData>({
    resolver: zodResolver(batchFormSchema),
    defaultValues: defaultBatchValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const batchValues = useWatch({ control: batchControl }) as Partial<BatchFormData>;

  /** Live preview — the SAME expansion the reducer writes (write order). */
  const batchPreview = useMemo(() => {
    if (!dataset) return null;
    const kind = batchValues.kind ?? 'markup_pct';
    const adjustment =
      kind === 'fixed'
        ? batchValues.fixedPrice != null && batchValues.fixedPrice > 0 && batchValues.currency
          ? ({ kind: 'fixed', fixedPrice: batchValues.fixedPrice, currency: batchValues.currency } as const)
          : null
        : batchValues.markupPct != null && batchValues.markupPct > 0
          ? ({ kind: 'markup_pct', markupPct: batchValues.markupPct } as const)
          : null;
    return buildSpecialPriceBatchPreview(dataset, {
      productIds: batchValues.productIds ?? [],
      customerIds: batchValues.customerIds ?? [],
      adjustment,
      validFrom: batchValues.validFrom ?? dayjs().format('YYYY-MM-DD'),
      validTo: batchValues.validTo ?? null,
    });
  }, [dataset, batchValues]);

  const openBatch = () => {
    resetBatch({
      ...defaultBatchValues,
      validFrom: dayjs().format('YYYY-MM-DD'),
      // The S3.2 prefilter preselects its product in the batch picker too.
      productIds: productParam ? [productParam] : [],
    });
    setBatchOpen(true);
  };

  const closeBatch = () => {
    setBatchOpen(false);
    resetBatch(defaultBatchValues);
  };

  /**
   * Batch form OK → the UI-level refusals (cap / overlap) fire BEFORE the
   * reason step; the store guards are the hard backstop.
   */
  const onBatchValid = (values: BatchFormData) => {
    if (!batchPreview) return;
    if (batchPreview.capExceeded) {
      message.error(
        t('pricing:pricing.overrides.batch.messages.capExceeded', {
          cap: SPECIAL_PRICE_BATCH_CAP,
        })
      );
      return;
    }
    if (batchPreview.comboCount === 0) {
      message.error(t('pricing:pricing.overrides.batch.messages.empty'));
      return;
    }
    if (batchPreview.overlapCount > 0) {
      message.error(t('pricing:pricing.overrides.batch.messages.overlapBlocked'));
      return;
    }
    if (batchPreview.unresolvableCount > 0) {
      message.error(t('pricing:pricing.overrides.batch.messages.unresolvableBlocked'));
      return;
    }
    const input: SpecialPriceBatchInput = {
      productIds: values.productIds,
      customerIds: values.customerIds,
      adjustment:
        values.kind === 'fixed'
          ? {
              kind: 'fixed',
              fixedPrice: values.fixedPrice ?? 0,
              currency: values.currency ?? 'AUD',
            }
          : { kind: 'markup_pct', markupPct: values.markupPct ?? 0 },
      validFrom: values.validFrom,
      validTo: values.validTo,
    };
    setPendingAction({ type: 'batch', input });
  };

  const openCreate = () => {
    setEditingRow(null);
    reset(defaultFormValues);
    setModalOpen(true);
  };

  const openEdit = (row: SpecialItemPrice) => {
    setEditingRow(row);
    reset({
      customerId: row.customerId,
      productId: row.productId,
      kind: row.kind,
      fixedPrice: row.kind === 'fixed' ? row.fixedPrice : null,
      currency: row.kind === 'fixed' ? row.currency : null,
      markupPct: row.kind === 'markup_pct' ? row.markupPct : null,
      validFrom: row.validFrom,
      validTo: row.validTo,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRow(null);
    reset(defaultFormValues);
  };

  /** Form OK → build the row, then demand the reason (never save without it). */
  const onFormValid = (values: OverrideFormData) => {
    const base = {
      id: editingRow?.id ?? `sip-${Date.now()}`,
      customerId: values.customerId,
      productId: values.productId,
      reason: editingRow?.reason ?? '',
      validFrom: values.validFrom,
      validTo: values.validTo,
      isActive: editingRow?.isActive ?? true,
      createdBy: editingRow?.createdBy ?? '',
      createdAt: editingRow?.createdAt ?? new Date().toISOString(),
    };
    const row: SpecialItemPrice =
      values.kind === 'fixed'
        ? {
            ...base,
            kind: 'fixed',
            fixedPrice: values.fixedPrice ?? 0,
            currency: values.currency ?? 'AUD',
          }
        : {
            ...base,
            kind: 'markup_pct',
            markupPct: values.markupPct ?? 0,
            // B0 — an EDIT re-stamps at save time on purpose: editing the
            // % re-authors the row, so it belongs to the basis in force
            // now. Option B (2026-08-31): that basis is the product's
            // EFFECTIVE anchor, which is the figure the resolver will
            // multiply. `dataset` is always present on this path (the tab
            // renders its table from it); the fallback is the engine's own
            // `pricing_base` default, never a fabricated anchor.
            // See SpecialItemMarkupPrice in src/types/api/pricing.ts.
            basisAtAuthoring: dataset
              ? effectiveAnchorOf(dataset, values.productId)
              : DEFAULT_PRICING_ANCHOR,
          };
    setPendingAction({ type: 'save', row });
  };

  const onReasonSubmit = (reason: string) => {
    if (!pendingAction) return;
    try {
      if (pendingAction.type === 'save') {
        saveSpecialPrice(pendingAction.row, reason);
        message.success(t('pricing:pricing.overrides.messages.saved'));
        closeModal();
      } else if (pendingAction.type === 'batch') {
        const count =
          pendingAction.input.productIds.length * pendingAction.input.customerIds.length;
        saveSpecialPriceBatch(pendingAction.input, reason);
        message.success(t('pricing:pricing.overrides.batch.messages.applied', { count }));
        closeBatch();
      } else {
        setSpecialPriceActive(pendingAction.id, pendingAction.isActive, reason);
        message.success(t('pricing:pricing.overrides.messages.statusUpdated'));
      }
      setPendingAction(null);
    } catch (guardError) {
      if (guardError instanceof PricingGuardError && guardError.code === 'BATCH_OVERLAP') {
        message.error(t('pricing:pricing.overrides.batch.messages.overlapBlocked'));
      } else if (
        guardError instanceof PricingGuardError &&
        guardError.code === 'BATCH_UNRESOLVABLE'
      ) {
        message.error(t('pricing:pricing.overrides.batch.messages.unresolvableBlocked'));
      } else if (
        guardError instanceof PricingGuardError &&
        guardError.code === 'BATCH_CAP_EXCEEDED'
      ) {
        message.error(
          t('pricing:pricing.overrides.batch.messages.capExceeded', {
            cap: SPECIAL_PRICE_BATCH_CAP,
          })
        );
      } else {
        message.error(t('pricing:pricing.overrides.messages.reasonRequired'));
      }
    }
  };

  const valueCell = (row: SpecialItemPrice) =>
    row.kind === 'fixed' ? (
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: token.fontWeightStrong }}>
        {formatMoney(row.currency, row.fixedPrice)}
      </span>
    ) : (
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
        {/* Names the row's OWN recorded basis, not the anchor in force today
            — that is the figure the resolver multiplies for this row
            (`basisAtAuthoring`, read by the engine since the Option-B flip). */}
        {t('pricing:pricing.overrides.valueOfBase', {
          pct: row.markupPct,
          basis: t(`pricing:pricing.anchor.${row.basisAtAuthoring}`),
        })}
      </span>
    );

  const columns: TableProps<SpecialItemPrice>['columns'] = [
    {
      title: t('pricing:pricing.overrides.fields.customer'),
      key: 'customer',
      width: 200,
      render: (_: unknown, row) => (
        <HighlightText text={customerName(row.customerId)} searchTerm={search} fallback="-" />
      ),
    },
    {
      title: t('pricing:pricing.overrides.fields.product'),
      key: 'product',
      width: 220,
      render: (_: unknown, row) => (
        <HighlightText text={productName(row.productId)} searchTerm={search} fallback="-" />
      ),
    },
    {
      title: t('pricing:pricing.overrides.fields.kind'),
      dataIndex: 'kind',
      key: 'kind',
      width: 110,
      render: (kind: SpecialItemPrice['kind']) => (
        <Tag color={kind === 'fixed' ? 'blue' : 'purple'} style={{ margin: 0 }}>
          {t(`pricing:pricing.overrideKind.${kind === 'fixed' ? 'fixed' : 'markupPct'}`)}
        </Tag>
      ),
    },
    {
      title: t('pricing:pricing.overrides.fields.value'),
      key: 'value',
      width: 170,
      align: 'right',
      render: (_: unknown, row) => valueCell(row),
    },
    {
      title: t('pricing:pricing.overrides.fields.validity'),
      key: 'validity',
      width: 200,
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.validFrom} — {row.validTo ?? t('pricing:pricing.overrides.openEnded')}
        </span>
      ),
    },
    {
      title: t('pricing:pricing.overrides.fields.reason'),
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (value: string) =>
        value ? <HighlightText text={value} searchTerm={search} fallback="-" /> : renderDash(),
    },
    {
      title: t('pricing:pricing.overrides.fields.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'} style={{ margin: 0 }}>
          {t(`pricing:pricing.overrides.status.${isActive ? 'active' : 'inactive'}`)}
        </Tag>
      ),
    },
    {
      title: t('pricing:pricing.overrides.fields.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, row) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                label: t('pricing:pricing.overrides.actions.edit'),
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  openEdit(row);
                },
              },
              {
                key: 'toggle',
                label: row.isActive
                  ? t('pricing:pricing.overrides.actions.deactivate')
                  : t('pricing:pricing.overrides.actions.activate'),
                danger: row.isActive,
                style: !row.isActive ? { color: token.colorSuccess } : undefined,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  setPendingAction({ type: 'toggle', id: row.id, isActive: !row.isActive });
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            aria-label={t('pricing:pricing.overrides.actions.actionsFor', { id: row.id })}
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  const auditColumns: TableProps<PriceOverrideAuditRow>['columns'] = [
    {
      title: t('pricing:pricing.overrides.audit.fields.when'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (value: string) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {dayjs(value).format('YYYY-MM-DD HH:mm')}
        </span>
      ),
    },
    {
      title: t('pricing:pricing.overrides.audit.fields.actor'),
      dataIndex: 'actorName',
      key: 'actorName',
      width: 140,
    },
    {
      title: t('pricing:pricing.overrides.audit.fields.change'),
      dataIndex: 'summary',
      key: 'summary',
      render: (value: string) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>,
    },
    {
      title: t('pricing:pricing.overrides.audit.fields.reason'),
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
    },
  ];

  const fieldErrorText = (key?: string) =>
    key ? <Text style={formStyles.errorStyle}>{t(key)}</Text> : null;

  /** Engine-resolved money cell — dash for unresolved/blank, never 0-as-data. */
  const resolutionCell = (resolution: PriceResolution | null) =>
    resolution && resolution.unitPrice !== null ? (
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatMoney(resolution.currency ?? '', resolution.unitPrice)}
      </span>
    ) : (
      renderDash()
    );

  const batchPreviewColumns: TableProps<SpecialPriceBatchPreviewRow>['columns'] = [
    {
      title: t('pricing:pricing.overrides.fields.customer'),
      key: 'customer',
      width: 220,
      render: (_: unknown, row) => customerName(row.customerId),
    },
    {
      title: t('pricing:pricing.overrides.fields.product'),
      key: 'product',
      width: 220,
      render: (_: unknown, row) => productName(row.productId),
    },
    {
      title: t('pricing:pricing.overrides.batch.previewCurrent'),
      key: 'current',
      width: 170,
      align: 'right',
      render: (_: unknown, row) => resolutionCell(row.current),
    },
    {
      title: t('pricing:pricing.overrides.batch.previewProposed'),
      key: 'proposed',
      width: 170,
      align: 'right',
      render: (_: unknown, row) => resolutionCell(row.proposed),
    },
    {
      title: t('pricing:pricing.overrides.batch.previewStatus'),
      key: 'status',
      width: 170,
      // Precedence (RULING-5/6): overlap → no-cost-record (markup drafts
      // only) → "Will write". Both refusal flags block Apply.
      render: (_: unknown, row) =>
        row.overlap ? (
          <Tag color="error" style={{ margin: 0 }}>
            {t('pricing:pricing.overrides.batch.overlapTag')}
          </Tag>
        ) : row.noCostRecord && (batchValues.kind ?? 'markup_pct') === 'markup_pct' ? (
          <Tag color="error" style={{ margin: 0 }}>
            {t('pricing:pricing.overrides.batch.noCostRecordTag')}
          </Tag>
        ) : (
          <Tag color="success" style={{ margin: 0 }}>
            {t('pricing:pricing.overrides.batch.newTag')}
          </Tag>
        ),
    },
  ];

  return (
    <>
      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Input
          placeholder={t('pricing:pricing.overrides.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
        {/* S3.2 — query-param product prefilter chip (closable) */}
        {productParam && (
          <Tag
            closable
            onClose={(e) => {
              e.preventDefault();
              clearProductFilter();
            }}
            color="purple"
            style={{ margin: 0 }}
          >
            {t('pricing:pricing.overrides.productFilter.label', {
              name: productName(productParam),
            })}
          </Tag>
        )}
        {/* DA-7 — batch seat is visibility-gated (hide-don't-disable) */}
        {batchSeat && (
          <Button
            size="large"
            icon={<AppstoreAddOutlined />}
            onClick={openBatch}
            style={{
              marginLeft: 'auto',
              borderRadius: radiusTokens.card,
              height: `${controlHeightTokens.controlHeightXL}px`,
            }}
          >
            {t('pricing:pricing.overrides.batch.openButton')}
          </Button>
        )}
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={openCreate}
          style={{
            marginLeft: batchSeat ? undefined : 'auto',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        >
          {t('pricing:pricing.overrides.addButton')}
        </Button>
      </div>

      <TableContainer
        errorState={{
          active: !!error && !dataset,
          title: t('pricing:pricing.overrides.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<SpecialItemPrice>
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={isLoading}
          rowClassName={(record) => (!record.isActive ? 'inactive-row' : '')}
          locale={{
            emptyText: search.trim() ? (
              <ListEmptyState
                title={t('pricing:pricing.overrides.states.noResults')}
                actionLabel={t('pricing:pricing.overrides.states.clearFilters')}
                onAction={() => setSearch('')}
                actionType="default"
              />
            ) : (
              <ListEmptyState title={t('pricing:pricing.overrides.states.noRows')} />
            ),
          }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1280 }}
        />
      </TableContainer>

      {/* PRICE_OVERRIDE review lens — the「報表送出給高權限看過」rows */}
      <div className="tw-mt-8">
        <Title level={5} style={{ marginBottom: token.marginXXS }}>
          {t('pricing:pricing.overrides.audit.title')}
        </Title>
        <Text
          type="secondary"
          style={{ fontSize: token.fontSizeSM, display: 'block', marginBottom: token.marginSM }}
        >
          {t('pricing:pricing.overrides.audit.hint')}
        </Text>
        <TableContainer>
          <Table<PriceOverrideAuditRow>
            columns={auditColumns}
            dataSource={auditRows}
            rowKey="id"
            size="small"
            locale={{
              emptyText: (
                <ListEmptyState title={t('pricing:pricing.overrides.audit.states.noRows')} />
              ),
            }}
            pagination={{ pageSize: 5, showSizeChanger: false }}
            scroll={{ x: 900 }}
          />
        </TableContainer>
      </div>

      {/* Create/Edit modal — OK leads into the mandatory-reason step */}
      <Modal
        open={modalOpen}
        title={
          editingRow
            ? t('pricing:pricing.overrides.modal.editTitle')
            : t('pricing:pricing.overrides.modal.createTitle')
        }
        onCancel={closeModal}
        width={640}
        footer={[
          <Button
            key="cancel"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={closeModal}
          >
            {t('common:actions.cancel')}
          </Button>,
          <Button
            key="next"
            type="primary"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={() => void handleSubmit(onFormValid)()}
          >
            {editingRow ? t('common:actions.save') : t('common:actions.add')}
          </Button>,
        ]}
      >
        <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '32px', padding: '20px' }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('pricing:pricing.overrides.modal.customerLabel')}
              </Text>
              <Controller
                name="customerId"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value || undefined}
                    onChange={(value) => {
                      field.onChange(value);
                      trigger('customerId');
                    }}
                    onBlur={field.onBlur}
                    size="large"
                    showSearch
                    optionFilterProp="label"
                    options={customers.map((customer) => ({
                      label: customer.customerName,
                      value: customer.customerId,
                    }))}
                    status={errors.customerId ? 'error' : undefined}
                    style={{ ...formStyles.inputStyle, width: '100%' }}
                    aria-label={t('pricing:pricing.overrides.modal.customerLabel')}
                  />
                )}
              />
              {fieldErrorText(errors.customerId?.message)}
            </Col>
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('pricing:pricing.overrides.modal.productLabel')}
              </Text>
              <Controller
                name="productId"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value || undefined}
                    onChange={(value) => {
                      field.onChange(value);
                      trigger('productId');
                    }}
                    onBlur={field.onBlur}
                    size="large"
                    showSearch
                    optionFilterProp="label"
                    options={products.map((product) => ({
                      label: product.displayName,
                      value: product.id,
                    }))}
                    status={errors.productId ? 'error' : undefined}
                    style={{ ...formStyles.inputStyle, width: '100%' }}
                    aria-label={t('pricing:pricing.overrides.modal.productLabel')}
                  />
                )}
              />
              {fieldErrorText(errors.productId?.message)}
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
            <Col span={24}>
              <Text style={formStyles.labelStyle}>
                {t('pricing:pricing.overrides.modal.kindLabel')}
              </Text>
              <Controller
                name="kind"
                control={control}
                render={({ field }) => (
                  <Radio.Group
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      trigger('kind');
                    }}
                    options={[
                      { label: t('pricing:pricing.overrideKind.fixed'), value: 'fixed' },
                      { label: t('pricing:pricing.overrideKind.markupPct'), value: 'markup_pct' },
                    ]}
                    optionType="button"
                    buttonStyle="solid"
                    size="large"
                  />
                )}
              />
            </Col>
          </Row>

          {watchedKind === 'fixed' ? (
            <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('pricing:pricing.overrides.modal.fixedPriceLabel')}
                </Text>
                <Controller
                  name="fixedPrice"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? null)}
                      onBlur={field.onBlur}
                      size="large"
                      min={0}
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      status={errors.fixedPrice ? 'error' : undefined}
                      aria-label={t('pricing:pricing.overrides.modal.fixedPriceLabel')}
                    />
                  )}
                />
                {fieldErrorText(errors.fixedPrice?.message)}
              </Col>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('pricing:pricing.overrides.modal.currencyLabel')}
                </Text>
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value ?? undefined}
                      onChange={(value) => {
                        field.onChange(value);
                        trigger('currency');
                      }}
                      onBlur={field.onBlur}
                      size="large"
                      showSearch
                      optionFilterProp="label"
                      options={currencySelectOptions}
                      status={errors.currency ? 'error' : undefined}
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      aria-label={t('pricing:pricing.overrides.modal.currencyLabel')}
                    />
                  )}
                />
                {fieldErrorText(errors.currency?.message)}
              </Col>
            </Row>
          ) : (
            <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('pricing:pricing.overrides.modal.markupPctLabel')}
                </Text>
                <Controller
                  name="markupPct"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? null)}
                      onBlur={field.onBlur}
                      size="large"
                      min={0}
                      suffix="%"
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      status={errors.markupPct ? 'error' : undefined}
                      aria-label={t('pricing:pricing.overrides.modal.markupPctLabel')}
                    />
                  )}
                />
                {fieldErrorText(errors.markupPct?.message)}
                <Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block' }}>
                  {t('pricing:pricing.overrides.modal.markupPctHint')}
                </Text>
              </Col>
            </Row>
          )}

          <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('pricing:pricing.overrides.modal.validFromLabel')}
              </Text>
              <Controller
                name="validFrom"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(value) => {
                      field.onChange(value ? value.format('YYYY-MM-DD') : '');
                      trigger('validFrom');
                    }}
                    size="large"
                    style={{ ...formStyles.inputStyle, width: '100%' }}
                    status={errors.validFrom ? 'error' : undefined}
                    aria-label={t('pricing:pricing.overrides.modal.validFromLabel')}
                  />
                )}
              />
              {fieldErrorText(errors.validFrom?.message)}
            </Col>
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                {t('pricing:pricing.overrides.modal.validToLabel')}
              </Text>
              <Controller
                name="validTo"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(value) => {
                      field.onChange(value ? value.format('YYYY-MM-DD') : null);
                      trigger('validTo');
                    }}
                    size="large"
                    style={{ ...formStyles.inputStyle, width: '100%' }}
                    status={errors.validTo ? 'error' : undefined}
                    aria-label={t('pricing:pricing.overrides.modal.validToLabel')}
                  />
                )}
              />
              {fieldErrorText(errors.validTo?.message)}
            </Col>
          </Row>
        </div>
      </Modal>

      {/* S3.1 — central batch (DA-7 write seat only; hide-don't-disable) */}
      {batchSeat && (
        <Modal
          open={batchOpen}
          title={t('pricing:pricing.overrides.batch.modalTitle')}
          onCancel={closeBatch}
          width={960}
          footer={[
            <Button
              key="cancel"
              size="large"
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
              onClick={closeBatch}
            >
              {t('common:actions.cancel')}
            </Button>,
            <Button
              key="apply"
              type="primary"
              size="large"
              style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
              onClick={() => void handleBatchSubmit(onBatchValid)()}
            >
              {t('pricing:pricing.overrides.batch.applyButton', {
                count: batchPreview?.comboCount ?? 0,
              })}
            </Button>,
          ]}
        >
          <Text
            type="secondary"
            style={{ fontSize: token.fontSizeSM, display: 'block', marginBottom: token.marginSM }}
          >
            {t('pricing:pricing.overrides.batch.hint', { cap: SPECIAL_PRICE_BATCH_CAP })}
          </Text>

          <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '20px', padding: '20px' }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('pricing:pricing.overrides.batch.productsLabel')}
                </Text>
                <Controller
                  name="productIds"
                  control={batchControl}
                  render={({ field }) => (
                    <Select
                      mode="multiple"
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        triggerBatch('productIds');
                      }}
                      onBlur={field.onBlur}
                      size="large"
                      showSearch
                      optionFilterProp="label"
                      maxTagCount="responsive"
                      options={products.map((product) => ({
                        label: product.displayName,
                        value: product.id,
                      }))}
                      status={batchErrors.productIds ? 'error' : undefined}
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      aria-label={t('pricing:pricing.overrides.batch.productsLabel')}
                    />
                  )}
                />
                {fieldErrorText(batchErrors.productIds?.message)}
              </Col>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('pricing:pricing.overrides.batch.customersLabel')}
                </Text>
                <Controller
                  name="customerIds"
                  control={batchControl}
                  render={({ field }) => (
                    <Select
                      mode="multiple"
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        triggerBatch('customerIds');
                      }}
                      onBlur={field.onBlur}
                      size="large"
                      showSearch
                      optionFilterProp="label"
                      maxTagCount="responsive"
                      options={customers.map((customer) => ({
                        label: customer.customerName,
                        value: customer.customerId,
                      }))}
                      status={batchErrors.customerIds ? 'error' : undefined}
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      aria-label={t('pricing:pricing.overrides.batch.customersLabel')}
                    />
                  )}
                />
                {fieldErrorText(batchErrors.customerIds?.message)}
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
              <Col span={24}>
                <Text style={formStyles.labelStyle}>
                  {t('pricing:pricing.overrides.batch.kindLabel')}
                </Text>
                <Controller
                  name="kind"
                  control={batchControl}
                  render={({ field }) => (
                    <Radio.Group
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        triggerBatch('kind');
                      }}
                      options={[
                        {
                          label: t('pricing:pricing.overrideKind.markupPct'),
                          value: 'markup_pct',
                        },
                        // FREEZE vocabulary (DA-2 amended / A2): a fixed
                        // batch price is a freeze, never a "round".
                        {
                          label: t('pricing:pricing.overrides.batch.freezeKind'),
                          value: 'fixed',
                        },
                      ]}
                      optionType="button"
                      buttonStyle="solid"
                      size="large"
                    />
                  )}
                />
                {batchValues.kind === 'fixed' && (
                  <Text
                    type="secondary"
                    style={{ fontSize: token.fontSizeSM, display: 'block', marginTop: '4px' }}
                  >
                    {t('pricing:pricing.overrides.batch.freezeNote')}
                  </Text>
                )}
              </Col>
            </Row>

            {batchValues.kind === 'fixed' ? (
              <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
                <Col span={12}>
                  <Text style={formStyles.labelStyle}>
                    <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                    {t('pricing:pricing.overrides.batch.frozenPriceLabel')}
                  </Text>
                  <Controller
                    name="fixedPrice"
                    control={batchControl}
                    render={({ field }) => (
                      <InputNumber
                        value={field.value}
                        onChange={(value) => field.onChange(value ?? null)}
                        onBlur={field.onBlur}
                        size="large"
                        min={0}
                        style={{ ...formStyles.inputStyle, width: '100%' }}
                        status={batchErrors.fixedPrice ? 'error' : undefined}
                        aria-label={t('pricing:pricing.overrides.batch.frozenPriceLabel')}
                      />
                    )}
                  />
                  {fieldErrorText(batchErrors.fixedPrice?.message)}
                </Col>
                <Col span={12}>
                  <Text style={formStyles.labelStyle}>
                    <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                    {t('pricing:pricing.overrides.modal.currencyLabel')}
                  </Text>
                  <Controller
                    name="currency"
                    control={batchControl}
                    render={({ field }) => (
                      <Select
                        value={field.value ?? undefined}
                        onChange={(value) => {
                          field.onChange(value);
                          triggerBatch('currency');
                        }}
                        onBlur={field.onBlur}
                        size="large"
                        showSearch
                        optionFilterProp="label"
                        options={currencySelectOptions}
                        status={batchErrors.currency ? 'error' : undefined}
                        style={{ ...formStyles.inputStyle, width: '100%' }}
                        aria-label={t('pricing:pricing.overrides.modal.currencyLabel')}
                      />
                    )}
                  />
                  {fieldErrorText(batchErrors.currency?.message)}
                </Col>
              </Row>
            ) : (
              <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
                <Col span={12}>
                  <Text style={formStyles.labelStyle}>
                    <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                    {t('pricing:pricing.overrides.modal.markupPctLabel')}
                  </Text>
                  <Controller
                    name="markupPct"
                    control={batchControl}
                    render={({ field }) => (
                      <InputNumber
                        value={field.value}
                        onChange={(value) => field.onChange(value ?? null)}
                        onBlur={field.onBlur}
                        size="large"
                        min={0}
                        suffix="%"
                        style={{ ...formStyles.inputStyle, width: '100%' }}
                        status={batchErrors.markupPct ? 'error' : undefined}
                        aria-label={t('pricing:pricing.overrides.modal.markupPctLabel')}
                      />
                    )}
                  />
                  {fieldErrorText(batchErrors.markupPct?.message)}
                  <Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block' }}>
                    {t('pricing:pricing.overrides.modal.markupPctHint')}
                  </Text>
                </Col>
              </Row>
            )}

            <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('pricing:pricing.overrides.modal.validFromLabel')}
                </Text>
                <Controller
                  name="validFrom"
                  control={batchControl}
                  render={({ field }) => (
                    <DatePicker
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(value) => {
                        field.onChange(value ? value.format('YYYY-MM-DD') : '');
                        triggerBatch('validFrom');
                      }}
                      size="large"
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      status={batchErrors.validFrom ? 'error' : undefined}
                      aria-label={t('pricing:pricing.overrides.modal.validFromLabel')}
                    />
                  )}
                />
                {fieldErrorText(batchErrors.validFrom?.message)}
              </Col>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  {t('pricing:pricing.overrides.modal.validToLabel')}
                </Text>
                <Controller
                  name="validTo"
                  control={batchControl}
                  render={({ field }) => (
                    <DatePicker
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(value) => {
                        field.onChange(value ? value.format('YYYY-MM-DD') : null);
                        triggerBatch('validTo');
                      }}
                      size="large"
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      status={batchErrors.validTo ? 'error' : undefined}
                      aria-label={t('pricing:pricing.overrides.modal.validToLabel')}
                    />
                  )}
                />
                {fieldErrorText(batchErrors.validTo?.message)}
              </Col>
            </Row>
          </div>

          {/* PREVIEW — exactly the rows to be written, in write order */}
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <Text strong>{t('pricing:pricing.overrides.batch.previewTitle')}</Text>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('pricing:pricing.overrides.batch.comboCount', {
                count: batchPreview?.comboCount ?? 0,
                cap: SPECIAL_PRICE_BATCH_CAP,
              })}
            </Text>
          </div>

          {batchPreview?.capExceeded && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: token.marginSM }}
              message={t('pricing:pricing.overrides.batch.alerts.capExceeded', {
                count: batchPreview.comboCount,
                cap: SPECIAL_PRICE_BATCH_CAP,
              })}
            />
          )}
          {!batchPreview?.capExceeded && (batchPreview?.overlapCount ?? 0) > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: token.marginSM }}
              message={t('pricing:pricing.overrides.batch.alerts.overlapBlocked', {
                count: batchPreview?.overlapCount ?? 0,
              })}
            />
          )}
          {!batchPreview?.capExceeded && (batchPreview?.unresolvableCount ?? 0) > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: token.marginSM }}
              message={t('pricing:pricing.overrides.batch.alerts.unresolvableBlocked', {
                count: batchPreview?.unresolvableCount ?? 0,
              })}
            />
          )}

          <Table<SpecialPriceBatchPreviewRow>
            columns={batchPreviewColumns}
            dataSource={batchPreview?.rows ?? []}
            rowKey="key"
            size="small"
            rowClassName={(row) =>
              row.overlap ||
              (row.noCostRecord && (batchValues.kind ?? 'markup_pct') === 'markup_pct')
                ? 'inactive-row'
                : ''
            }
            locale={{
              emptyText: batchPreview?.capExceeded ? (
                // NAMED cap-exceeded state — over the cap nothing is resolved.
                <ListEmptyState
                  title={t('pricing:pricing.overrides.batch.states.capExceeded', {
                    cap: SPECIAL_PRICE_BATCH_CAP,
                  })}
                />
              ) : (
                // NAMED empty-preview state.
                <ListEmptyState
                  title={t('pricing:pricing.overrides.batch.states.emptyPreview')}
                />
              ),
            }}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={{ x: 880 }}
          />
        </Modal>
      )}

      {/* The ONE reason gate — save and status changes both land here */}
      <ReasonRequiredModal
        open={pendingAction !== null}
        title={t('pricing:pricing.overrides.reasonModal.title')}
        description={
          pendingAction?.type === 'batch'
            ? t('pricing:pricing.overrides.batch.reasonDescription')
            : t('pricing:pricing.overrides.reasonModal.description')
        }
        submitText={t('pricing:pricing.overrides.reasonModal.submit')}
        cancelText={t('common:actions.cancel')}
        reasonLabel={t('pricing:pricing.overrides.reasonModal.reasonLabel')}
        reasonPlaceholder={t('pricing:pricing.overrides.reasonModal.reasonPlaceholder')}
        requiredMessage={t('pricing:pricing.overrides.reasonModal.requiredMessage')}
        onSubmit={onReasonSubmit}
        onCancel={() => setPendingAction(null)}
      />
    </>
  );
};

export default OverridesTab;
